// UserProgressHelper.ts
import {ALL_ACTION_ITEMS, ALL_CONTENT} from "../constants/LearningPathsConfig";
import {CHALLENGES_TYPES_CONFIG} from "../constants/ChallengesTypesConfig";


/**
 * Builds the default user progress object:
 * {
 *   courses: [
 *     {
 *       id: numberOrString,
 *       hasCompleted: false,
 *       lessons: [
 *         { id: numberOrString, hasCompleted: false, isInRoadmap: true },
 *         ...
 *       ]
 *     },
 *     ...
 *   ],
 *   interviews: [
 *     { id: ..., hasCompleted: false, isInRoadmap: true },
 *     ...
 *   ],
 *   challenges: [
 *     {
 *       id: ..., hasCompleted: false, isInRoadmap: true,
 *       challengeItems: [
 *         { id: ..., hasCompleted: false },
 *         ...
 *       ]
 *     },
 *     ...
 *   ],
 *   resources: [
 *     { id: ..., hasCompleted: false, isInRoadmap: true },
 *     ...
 *   ],
 *   tools: [
 *     { id: ..., hasCompleted: false, isInRoadmap: true },
 *     ...
 *   ]
 * }
 */
export function getDefaultUserProgress(userId: string, userExperimentId: string) {
    return {
        userId: userId,
        userExperimentId: userExperimentId,
        courses: buildDefaultCourses(),
        interviews: buildDefaultInterviews(),
        challenges: buildDefaultChallenges(),
        resources: buildDefaultResources(),
        tools: buildDefaultTools(),
        actions: buildDefaultActions()
    };
}

function buildDefaultCourses() {
    const result: any[] = [];
    ALL_CONTENT.allCourses.forEach((category) => {
        category.content.forEach((course) => {
            const courseProgress: any = {
                id: course.trackingId,
                hasCompleted: false,
                displayInRoadMap: true,
                lessons: []
            };

            course.sections.forEach((section) => {
                section.lessons.forEach((lesson) => {
                    courseProgress.lessons.push({
                        id: lesson.navUrl, // Use navUrl as unique identifier instead of lesson.id
                        hasCompleted: false,
                        displayInRoadMap: true
                    });
                });
            });

            result.push(courseProgress);
        });
    });
    return result;
}

function buildDefaultInterviews() {
    const result: any[] = [];
    // ALL_CONTENT.allInterviews is an array of categories; each category has .content (the interview objects)
    ALL_CONTENT.allInterviews.forEach((category) => {
        category.content.forEach((interview) => {
            result.push({
                id: interview.trackingId,
                hasCompleted: false,
                displayInRoadMap: true
            });
        });
    });
    return result;
}

function buildDefaultResources() {
    const result: any[] = [];
    ALL_CONTENT.allResources.forEach((category) => {
        category.content.forEach((resource) => {
            result.push({
                id: resource.trackingId,
                hasCompleted: false,
                displayInRoadMap: true
            });
        });
    });
    return result;
}

function buildDefaultTools() {
    const result: any[] = [];
    ALL_CONTENT.allAITools.forEach((category) => {
        category.content.forEach((tool) => {
            result.push({
                id: tool.trackingId,
                hasCompleted: false,
                displayInRoadMap: true
            });
        });
    });
    return result;
}

function buildDefaultChallenges() {
    const result: any[] = [];
    CHALLENGES_TYPES_CONFIG.forEach((challenge) => {
        const challengeProgress: any = {
            id: challenge.trackingId,
            hasCompleted: false,
            displayInRoadMap: true,
            challengeItems: []
        };

        challenge.challengeItems.forEach((challengeItem) => {
            challengeProgress.challengeItems.push({
                id: challengeItem.id,
                hasCompleted: false
            });
        });

        result.push(challengeProgress);
    });
    return result;
}

function buildDefaultActions() {
    const result: any[] = [];
    Object.values(ALL_ACTION_ITEMS).forEach((action) => {
        result.push({
            id: action.trackingId,
            hasCompleted: false,
            displayInRoadMap: true
        });
    });
    return result;
}


/**
 * Unified function to check if any content type or sub-item is completed
 * Automatically creates missing items with false defaults
 */
export function hasUserCompleted(userProgressData: any, contentType: string, contentId: string, subItemId?: string): boolean {
    if (!userProgressData) {
        return false;
    }

    // Ensure the content type array exists
    if (!userProgressData[contentType]) {
        userProgressData[contentType] = [];
    }

    // Find the main content item
    let contentItem = userProgressData[contentType].find((item: any) => item.id === contentId);
    
    // If main content item doesn't exist, create it
    if (!contentItem) {
        contentItem = {
            id: contentId,
            hasCompleted: false
        };
        
        // Add sub-items array for content types that need them
        if (contentType === 'courses') {
            contentItem.lessons = [];
        } else if (contentType === 'challenges') {
            contentItem.challengeItems = [];
        }
        
        userProgressData[contentType].push(contentItem);
    }

    if (subItemId === undefined) {
        return contentItem.hasCompleted || false;
    }

    let subItemArray: any[];
    if (contentType === 'courses') {
        subItemArray = contentItem.lessons = contentItem.lessons || [];
    } else if (contentType === 'challenges') {
        subItemArray = contentItem.challengeItems = contentItem.challengeItems || [];
    } else {
        return false;
    }

    // Find the sub-item
    let subItem = subItemArray.find((item: any) => item.id === subItemId);
    
    // If sub-item doesn't exist, create it
    if (!subItem) {
        subItem = {
            id: subItemId,
            hasCompleted: false
        };
        
        // Add displayInRoadMap for lessons
        if (contentType === 'courses') {
            subItem.displayInRoadMap = true;
        }
        
        subItemArray.push(subItem);
    }

    return subItem.hasCompleted || false;
}

/**
 * Convenience functions for backward compatibility and cleaner API
 */
export function hasUserCompletedLesson(userProgressData: any, courseTrackingId: string, lessonId: string): boolean { // Changed from number to string
    return hasUserCompleted(userProgressData, 'courses', courseTrackingId, lessonId);
}

export function hasUserCompletedChallengeItem(userProgressData: any, challengeTrackingId: string, challengeItemId: string): boolean {
    return hasUserCompleted(userProgressData, 'challenges', challengeTrackingId, challengeItemId);
}

export function hasUserCompletedCourse(userProgressData: any, courseId: string): boolean {
    return hasUserCompleted(userProgressData, 'courses', courseId);
}

export function hasUserCompletedInterview(userProgressData: any, interviewId: string): boolean {
    return hasUserCompleted(userProgressData, 'interviews', interviewId);
}

export function hasUserCompletedChallenge(userProgressData: any, challengeId: string): boolean {
    return hasUserCompleted(userProgressData, 'challenges', challengeId);
}

export function hasUserCompletedResource(userProgressData: any, resourceId: string): boolean {
    return hasUserCompleted(userProgressData, 'resources', resourceId);
}

export function hasUserCompletedTool(userProgressData: any, toolId: string): boolean {
    return hasUserCompleted(userProgressData, 'tools', toolId);
}

export function hasUserCompletedAction(userProgressData: any, actionId: string): boolean {
    return hasUserCompleted(userProgressData, 'actions', actionId);
}

/**
 * Get progress for a specific course (percentage of lessons completed)
 */
export function getCourseProgress(userProgressData: any, courseTrackingId: string): number {
    if (!userProgressData || !userProgressData.courses) return 0;
    
    const course = userProgressData.courses.find((course: any) => course.id === courseTrackingId);
    if (!course || !course.lessons || course.lessons.length === 0) return 0;
    
    const completedLessons = course.lessons.filter((lesson: any) => lesson.hasCompleted).length;
    return Math.round((completedLessons / course.lessons.length) * 100);
}

/**
 * Get progress for a specific challenge (percentage of challenge items completed)
 */
export function getChallengeProgress(userProgressData: any, challengeTrackingId: string): number {
    if (!userProgressData || !userProgressData.challenges) return 0;
    
    const challenge = userProgressData.challenges.find((challenge: any) => challenge.id === challengeTrackingId);
    if (!challenge || !challenge.challengeItems || challenge.challengeItems.length === 0) return 0;
    
    const completedItems = challenge.challengeItems.filter((item: any) => item.hasCompleted).length;
    return Math.round((completedItems / challenge.challengeItems.length) * 100);
}
